import { useSearch, Link } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { AuthBrandHeader } from "@/components/AuthBrandHeader";
import { useAuth } from "@/contexts/AuthContext";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { ArrowLeft } from "lucide-react";
import { FONT } from "@shared/brand";
import { useGuardedSignOut } from "@/components/LogoutSaveCheck";

/**
 * Email-verification RESULT page (XC-24). The server's GET /api/auth/verify-email/:token
 * verifies the token session-independently (the link proves email ownership) and then
 * redirects here with ?email=<verified address>. This page renders in BOTH auth states
 * (it is a top-level public route in App.tsx, before the auth gates) and branches on
 * whether — and as whom — the visitor is logged in:
 *   (a) logged in as the verified email  → "verified, continue to dashboard"
 *   (b) logged out                       → "verified, go to login"
 *   (c) logged in as a DIFFERENT email   → mismatch: verified, but log out and sign back
 *                                          in as the verified address (wrong-browser case)
 * The token is never re-checked here — verification already happened server-side.
 */
export default function VerifyEmailResult() {
  const search = useSearch();
  const { user, isAuthenticated, isLoading } = useAuth();

  const params = new URLSearchParams(search);
  const verifiedEmail = params.get("email") ?? "";
  // VERIFY-LINK-1 (s123): the verify route sends already-consumed tokens here with this flag so a
  // mail-scanner prefetch (which verified the account before the human clicked) shows a calm
  // reassurance rather than the alarming "invalid or expired" error.
  const alreadyVerified = params.get("status") === "already_verified";
  const sessionEmail = user?.email ?? "";
  const sameUser =
    isAuthenticated &&
    !!verifiedEmail &&
    sessionEmail.toLowerCase() === verifiedEmail.toLowerCase();
  const differentUser = isAuthenticated && !sameUser;

  // D-170.6: converted from a raw apiRequest. Class B — logging out twice lands on the same
  // end state, so no lock; the navigation below happens regardless of the result.
  const logoutMutation = useMutation({
    mutationFn: async () => await apiRequest("POST", "/api/auth/logout"),
  });

  // Case (c): drop the current (wrong) session and send them to login so they can sign
  // in as the verified address. Inline logout → /login (the context logout lands on "/").
  // s227 (LOGOUT-SAVE-CHECK-1): switching account signs out, so it asks first when the Secure File has unsaved changes.
  const guardedSignOut = useGuardedSignOut();
  const handleSwitchAccount = () => guardedSignOut(async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch {
      /* best-effort — navigate regardless */
    }
    queryClient.clear();
    window.location.href = "/login";
  });

  let title: string;
  let description: React.ReactNode;
  let action: React.ReactNode;

  if (isLoading) {
    // Hold until /api/auth/me resolves so we don't flash the wrong branch.
    title = "Verifying…";
    description = "One moment.";
    action = null;
  } else if (alreadyVerified) {
    // VERIFY-LINK-1 (s123): token already consumed (prefetch / second click). The account is
    // verified — reassure warmly, then send them to sign in (or the dashboard if already signed in).
    title = "You're already verified";
    description = (
      <>
        This link was already used — usually by your email provider's security scanner checking it
        before you clicked. If you created this account, you're all set: just sign in.
      </>
    );
    action = sameUser ? (
      <Link href="/dashboard">
        <Button type="button" className="w-full" data-testid="button-continue-dashboard">
          Continue to dashboard
        </Button>
      </Link>
    ) : (
      <Link href="/login">
        <Button type="button" className="w-full" data-testid="button-already-verified-login">
          Sign in
        </Button>
      </Link>
    );
  } else if (differentUser) {
    title = "Email verified — with a heads up";
    description = (
      <>
        This verification link is for{" "}
        <strong>{verifiedEmail || "another account"}</strong>. You're currently signed in as{" "}
        <strong>{sessionEmail}</strong>. The email has been verified — log out and sign back
        in as <strong>{verifiedEmail || "that account"}</strong> to continue.
      </>
    );
    action = (
      <Button type="button" className="w-full" onClick={handleSwitchAccount} data-testid="button-switch-account">
        Log out &amp; sign in as {verifiedEmail || "the verified account"}
      </Button>
    );
  } else if (sameUser) {
    title = "Email verified!";
    description = (
      <>
        Your email address{" "}
        {verifiedEmail ? (<><strong>{verifiedEmail}</strong> </>) : null}
        has been verified. You're all set.
      </>
    );
    action = (
      <Link href="/dashboard">
        <Button type="button" className="w-full" data-testid="button-continue-dashboard">
          Continue to dashboard
        </Button>
      </Link>
    );
  } else {
    // Case (b): logged out (incl. the common other-browser case).
    title = "Email verified!";
    description = (
      <>
        Your email address{" "}
        {verifiedEmail ? (<><strong>{verifiedEmail}</strong> </>) : null}
        has been verified. You can now log in.
      </>
    );
    action = (
      <Link href="/login">
        <Button type="button" className="w-full" data-testid="button-go-to-login">
          Go to login
        </Button>
      </Link>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <div className="flex flex-col items-center w-full max-w-md">
        <div className="mb-3">
          <Link href="/">
            <span className="text-sm text-muted-foreground hover:text-primary cursor-pointer" data-testid="link-back-home">
              <ArrowLeft className="h-3.5 w-3.5 inline-block align-[-2px]" aria-hidden="true" /> Back to home
            </span>
          </Link>
        </div>
        <AuthBrandHeader />
        <Card className="w-full">
          <CardHeader>
            <CardTitle style={{ fontFamily: FONT.serif }} data-testid="text-verify-result-title">
              {title}
            </CardTitle>
            <CardDescription data-testid="text-verify-result-description">{description}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">{action}</CardContent>
        </Card>
      </div>
    </div>
  );
}
